import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { Permission, usePermission } from '@bonusly/who-am-i';

export type ActivityType = 'bonus' | 'feedback' | 'agenda_topic' | 'goal' | 'goal_update';
export type EntityType = 'goal'; // Can extend to 'topic', 'project', etc. in the future

interface LinkActivityParams {
	entityType: EntityType;
	entityId: string;
	activityType: ActivityType;
	activityId: string;
}

interface UnlinkActivityParams {
	entityType: EntityType;
	entityId: string;
	activityType: ActivityType;
	activityId: string;
}

interface ActivityLinkResponse {
	id: string;
	title: string;
	activities: Array<{
		id: string;
		type: string; // Backend returns raw types like 'ConstructiveFeedback::Feedback'
	}>;
	// ... other entity data
}

interface ActivityLinkError {
	error?: string;
	errors?: string[];
}

/**
 * Hook for linking and unlinking activities to/from entities (goals, topics, etc.)
 * Currently supports goals, but designed to be extensible for other entity types.
 */
export function useActivityLinks() {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	const canAccessGoals = usePermission(Permission.CanAccessGoals);

	/**
	 * Map frontend activity types to backend activity types
	 */
	const mapToBackendActivityType = (activityType: ActivityType): string => {
		switch (activityType) {
			case 'feedback':
				return 'ConstructiveFeedback::Feedback';
			case 'bonus':
				return 'Bonus'; // Assuming bonus stays the same
			case 'agenda_topic':
				return 'agenda_topic'; // Assuming these stay the same
			case 'goal':
				return 'goal';
			case 'goal_update':
				return 'goal_update';
			default:
				return activityType; // Fallback to original
		}
	};

	/**
	 * Get the API endpoint for a given entity type
	 */
	const getEndpoint = (entityType: EntityType, entityId: string): string => {
		switch (entityType) {
			case 'goal':
				return `/achieve/goals/${entityId}/activities`;
			// Future: Add support for other entity types
			// case 'topic':
			//   return `/topics/${entityId}/activities`;
			// case 'project':
			//   return `/projects/${entityId}/activities`;
			default:
				throw new Error(`Unsupported entity type: ${entityType}`);
		}
	};

	/**
	 * Get the query key for caching the entity data
	 */
	const getQueryKey = (entityType: EntityType, entityId: string): string[] => {
		switch (entityType) {
			case 'goal':
				return ['goal', entityId];
			// Future: Add query keys for other entity types
			// case 'topic':
			//   return ['topic', entityId];
			default:
				return [entityType, entityId];
		}
	};

	/**
	 * Link an activity to an entity
	 */
	const linkMutation = useMutation<ActivityLinkResponse, ActivityLinkError, LinkActivityParams>({
		mutationFn: async ({ entityType, entityId, activityType, activityId }) => {
			// Check permission for Goals-related operations
			if (entityType === 'goal' && !canAccessGoals) {
				throw new Error('Access denied: Goals feature not available for your plan');
			}
			
			const endpoint = getEndpoint(entityType, entityId);

			const response = await http.post<ActivityLinkResponse>(
				endpoint,
				{
					activity_type: activityType,
					activity_id: activityId,
				},
				{
					headers: {
						'Key-Inflection': 'camel',
					},
				}
			);

			return response.data;
		},
		onSuccess: (data, variables) => {
			const queryKey = getQueryKey(variables.entityType, variables.entityId);
			queryClient.setQueryData(queryKey, data);

			// Also invalidate related queries that might show this entity
			queryClient.invalidateQueries({
				queryKey: [variables.entityType],
				exact: false,
			});
		},
		onError: (error) => {
			console.error('Failed to link activity:', error);
		},
	});

	/**
	 * Unlink an activity from an entity
	 */
	const unlinkMutation = useMutation<ActivityLinkResponse, ActivityLinkError, UnlinkActivityParams>({
		mutationFn: async ({ entityType, entityId, activityType, activityId }) => {
			// Check permission for Goals-related operations
			if (entityType === 'goal' && !canAccessGoals) {
				throw new Error('Access denied: Goals feature not available for your plan');
			}
			
			const endpoint = getEndpoint(entityType, entityId);

			const response = await http.delete<ActivityLinkResponse>(endpoint, {
				params: {
					activity_id: activityId,
					activity_type: activityType,
				},
				headers: {
					'Key-Inflection': 'camel',
				},
			});

			return response.data;
		},
		onSuccess: (data, variables) => {
			// Invalidate and update the entity query
			const queryKey = getQueryKey(variables.entityType, variables.entityId);
			queryClient.setQueryData(queryKey, data);

			// Also invalidate related queries that might show this entity
			queryClient.invalidateQueries({
				queryKey: [variables.entityType],
				exact: false,
			});
		},
		onError: (error) => {
			console.error('Failed to unlink activity:', error);
		},
	});

	/**
	 * Check if a specific activity is currently being linked
	 */
	const isActivityLinking = (activityId: string): boolean => {
		return linkMutation.isLoading && linkMutation.variables?.activityId === activityId;
	};

	/**
	 * Check if a specific activity is currently being unlinked
	 */
	const isActivityUnlinking = (activityId: string): boolean => {
		return unlinkMutation.isLoading && unlinkMutation.variables?.activityId === activityId;
	};

	/**
	 * Check if a specific activity is currently loading (linking or unlinking)
	 */
	const isActivityLoading = (activityId: string): boolean => {
		return isActivityLinking(activityId) || isActivityUnlinking(activityId);
	};

	return {
		// Functions
		link: linkMutation.mutate,
		linkAsync: linkMutation.mutateAsync,
		unlink: unlinkMutation.mutate,
		unlinkAsync: unlinkMutation.mutateAsync,

		// Global loading states (for backward compatibility)
		isLinking: linkMutation.isLoading,
		isUnlinking: unlinkMutation.isLoading,
		isLoading: linkMutation.isLoading || unlinkMutation.isLoading,

		// Per-item loading state functions
		isActivityLinking,
		isActivityUnlinking,
		isActivityLoading,

		// Error states
		linkError: linkMutation.error,
		unlinkError: unlinkMutation.error,
		error: linkMutation.error || unlinkMutation.error,

		// Success states
		linkSuccess: linkMutation.isSuccess,
		unlinkSuccess: unlinkMutation.isSuccess,

		// Reset functions
		reset: () => {
			linkMutation.reset();
			unlinkMutation.reset();
		},
		resetLink: linkMutation.reset,
		resetUnlink: unlinkMutation.reset,

		mapToBackendActivityType,
	};
}
